import { AnimeEpisode } from '../types/anime';
import {
  getOfflineEpisode,
  getSegment,
  offlineEpisodeId,
  OfflineEpisode,
  requestPersistentStorage,
  saveEpisodeMeta,
  saveSegment,
} from './animeOfflineDB';

export interface EpisodeDownloadProgress {
  episodeNum: number;
  doneSegments: number;
  totalSegments: number;
  bytes: number;
  status: 'resolving' | 'downloading' | 'completed';
  message: string;
}

export type DownloadSite = 'anime-waku' | 'animeruka';

export const SITE_LABELS: Record<DownloadSite, string> = {
  'anime-waku': 'Anime-Waku',
  animeruka: 'AnimeRuka',
};

export function getDownloadSite(url?: string): DownloadSite | null {
  if (!url) return null;
  if (/^https:\/\/anime-waku\.com\/ep\//.test(url)) return 'anime-waku';
  if (/^https:\/\/animeruka\.com\/ep\//.test(url)) return 'animeruka';
  return null;
}

/** Anime-Waku and AnimeRuka episodes can be downloaded (their players serve HLS MPEG-TS streams) */
export function isDownloadableEpisode(ep: AnimeEpisode): boolean {
  return getDownloadSite(ep.url) !== null;
}

export interface RukaPlayerOption {
  nume: string;
  label: string;
  host: string;
  downloadable: boolean;
}

/** The numbered players on an AnimeRuka / Anime-Waku episode page, and which of them the app can save */
export async function fetchRukaPlayers(epUrl: string, signal?: AbortSignal): Promise<RukaPlayerOption[]> {
  const res = await fetch(`/api/ruka/players?url=${encodeURIComponent(epUrl)}`, { signal });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.players)) throw new Error(data.error || `HTTP ${res.status}`);
  return data.players;
}

/** "AnimeRuka ตัวเล่น 1 (หลัก)" — the site, the player tab's number, and the name the site gives the tab */
export function playerName(p: { nume: string; label: string }, site: DownloadSite | null): string {
  const tab = `ตัวเล่น ${p.nume}`;
  const name = p.label === tab ? tab : `${tab} (${p.label})`;
  return site ? `${SITE_LABELS[site]} ${name}` : name;
}

// Segments in flight at once; each worker takes the next one as soon as it's done.
// Anime-Waku's segments come through our proxy and take 1-3 s each from the source, so speed grows almost
// in step with how many are in flight (measured: 6 -> ~5 MB/s, 24 -> ~17 MB/s). Both sites go over HTTP/2,
// so the 6-per-host limit doesn't apply.
const CONCURRENCY = 24;
/** Save the episode's progress (for resuming) at most this often, not after every segment */
const META_SAVE_MS = 2000;
const RETRIES = 3;

/**
 * Anime-Waku's player host prepends an image header to each MPEG-TS segment; drop everything before
 * the first run of TS sync bytes (0x47 every 188 bytes). Segments that are already clean pass through.
 */
function stripTsPrefix(buf: ArrayBuffer): ArrayBuffer {
  const u8 = new Uint8Array(buf);
  for (let i = 0; i + 188 * 4 < u8.length; i++) {
    if (
      u8[i] === 0x47 &&
      u8[i + 188] === 0x47 &&
      u8[i + 376] === 0x47 &&
      u8[i + 564] === 0x47 &&
      u8[i + 752] === 0x47
    ) {
      return i === 0 ? buf : buf.slice(i);
    }
  }
  return buf;
}

async function fetchSegment(url: string, signal: AbortSignal): Promise<ArrayBuffer> {
  let lastErr: any;
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    try {
      const res = await fetch(url, { signal, referrerPolicy: 'no-referrer' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return stripTsPrefix(await res.arrayBuffer());
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
      lastErr = err;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw new Error(`โหลดวิดีโอบางส่วนไม่สำเร็จ (${lastErr?.message || 'network'})`);
}

/**
 * Download one Anime-Waku or AnimeRuka episode into the app (IndexedDB, watched in the app's own player).
 * `prefer` is the player name to take it from; the server falls back to another player when
 * this episode doesn't have that one. `fallbackUrls` are the same episode on the other site,
 * tried when this one has nothing downloadable. The result says which site and player were used.
 * An interrupted download resumes where it stopped.
 */
export async function downloadEpisode(
  ep: AnimeEpisode,
  anime: { id: string; title: string },
  signal: AbortSignal,
  onProgress: (p: EpisodeDownloadProgress) => void,
  prefer = '',
  fallbackUrls: string[] = []
): Promise<{ title: string; bytes: number; source: string }> {
  onProgress({
    episodeNum: ep.number,
    doneSegments: 0,
    totalSegments: 0,
    bytes: 0,
    status: 'resolving',
    message: `กำลังหาไฟล์วิดีโอตอนที่ ${ep.number}...`,
  });

  const candidateUrls = [ep.url, ...fallbackUrls.filter((u) => u && u !== ep.url)];
  let data: any = {};
  let activeUrl = ep.url;
  let lastError = '';
  for (const candidateUrl of candidateUrls) {
    const res = await fetch(
      `/api/ruka/stream?url=${encodeURIComponent(candidateUrl)}&prefer=${encodeURIComponent(prefer)}`,
      { signal }
    );
    const parsed = await res.json().catch(() => ({}));
    if (res.ok && Array.isArray(parsed.segments) && parsed.segments.length > 0) {
      data = parsed;
      activeUrl = candidateUrl;
      break;
    }
    lastError = parsed.error || `ดึงข้อมูลวิดีโอไม่สำเร็จ (HTTP ${res.status})`;
  }
  if (!Array.isArray(data.segments) || data.segments.length === 0) {
    throw new Error(lastError || 'ดึงข้อมูลวิดีโอไม่สำเร็จ');
  }
  const segments: string[] = data.segments;
  const title: string = data.title || `${anime.title} ตอนที่ ${ep.number} ${ep.audio}`;
  const site = getDownloadSite(activeUrl);
  const source = data.player?.nume ? playerName(data.player, site) : site ? SITE_LABELS[site] : '';

  await requestPersistentStorage();
  const id = offlineEpisodeId(anime.id, ep.audio, ep.number);
  const existing = await getOfflineEpisode(id);
  // Same stream as before (same page and player, same length): keep the segments already in, fetch the rest
  const alreadySaved = new Set<number>();
  const sameSource =
    (!existing?.sourceUrl || existing.sourceUrl === activeUrl) &&
    (!existing?.source || !source || existing.source === source);
  if (existing && sameSource && existing.segmentCount === segments.length) {
    for (let i = 0; i < segments.length; i++) {
      if (await getSegment(id, i)) alreadySaved.add(i);
    }
  }
  const meta: OfflineEpisode = {
    id,
    animeId: anime.id,
    animeTitle: anime.title,
    number: ep.number,
    audio: ep.audio,
    title,
    sourceUrl: activeUrl,
    durations:
      Array.isArray(data.durations) && data.durations.length === segments.length
        ? data.durations
        : segments.map(() => (data.durationSec || 0) / segments.length),
    segmentCount: segments.length,
    savedSegments: alreadySaved.size,
    sizeBytes: alreadySaved.size > 0 ? existing?.sizeBytes || 0 : 0,
    downloadedAt: new Date().toISOString(),
    source: source || undefined,
  };
  await saveEpisodeMeta(meta);

  let bytes = meta.sizeBytes;
  let done = alreadySaved.size;
  const report = () =>
    onProgress({
      episodeNum: ep.number,
      doneSegments: done,
      totalSegments: segments.length,
      bytes,
      status: 'downloading',
      message: `กำลังโหลดตอนที่ ${ep.number} (${ep.audio})${source ? ` จาก ${source}` : ''}`,
    });
  report();

  const todo = segments.map((_, i) => i).filter((i) => !alreadySaved.has(i));
  // A pool of workers: no waiting for the slowest of a batch, and storing a segment doesn't hold up the next fetch
  let next = 0;
  let lastMetaSave = Date.now();
  let failure: unknown = null;
  const worker = async () => {
    while (next < todo.length && !failure) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const idx = todo[next++];
      const buf = await fetchSegment(segments[idx], signal);
      await saveSegment(id, idx, new Blob([buf], { type: 'video/mp2t' }));
      done++;
      bytes += buf.byteLength;
      report();
      if (Date.now() - lastMetaSave >= META_SAVE_MS) {
        lastMetaSave = Date.now();
        meta.savedSegments = done;
        meta.sizeBytes = bytes;
        await saveEpisodeMeta(meta);
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, todo.length) }, () =>
      worker().catch((err) => {
        // First failure stops the other workers; the saved segments still count for resuming
        failure ??= err;
      })
    )
  );
  meta.savedSegments = done;
  meta.sizeBytes = bytes;
  await saveEpisodeMeta(meta);
  if (failure) throw failure;

  onProgress({
    episodeNum: ep.number,
    doneSegments: segments.length,
    totalSegments: segments.length,
    bytes,
    status: 'completed',
    message: `ดาวน์โหลดตอนที่ ${ep.number} เสร็จแล้ว`,
  });
  return { title, bytes, source };
}

export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
  return `${Math.max(0, Math.round(n / 1024))} KB`;
}
